import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage } from './api';

/** Exécute `cb` toutes les `ms` millisecondes ; `ms === null` met en pause. */
export function useInterval(cb: () => void, ms: number | null): void {
  const ref = useRef(cb);
  useEffect(() => {
    ref.current = cb;
  });
  useEffect(() => {
    if (ms === null) return;
    const id = window.setInterval(() => ref.current(), ms);
    return () => window.clearInterval(id);
  }, [ms]);
}

/**
 * Charge une ressource au montage (et quand `key` change). `data` reste `null` pendant le chargement ; `error` porte le message
 * du serveur. `reload` relit sans vider l'affichage.
 */
export function useLoader<T>(fn: () => Promise<T>, key: unknown = null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fnRef = useRef(fn);
  const alive = useRef(true);
  useEffect(() => {
    fnRef.current = fn;
  });

  const reload = useCallback(async () => {
    try {
      const value = await fnRef.current();
      if (alive.current) {
        setData(value);
        setError(null);
      }
    } catch (e) {
      if (alive.current) setError(errorMessage(e));
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    setData(null);
    void reload();
    return () => {
      alive.current = false;
    };
  }, [reload, key]);

  return { data, error, reload, setData };
}

/** Exécute une mutation en suivant `busy` et l'erreur (message du serveur) ; retourne `undefined` en cas d'échec. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(errorMessage(e));
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, error, setError, run };
}

/** Horloge qui se rafraîchit toutes les `ms` millisecondes (pour recalculer des âges affichés). */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useInterval(() => setNow(Date.now()), ms);
  return now;
}
